import {
  AccessTime,
  AttachMoney,
  Calculate,
  DescriptionOutlined,
} from '@mui/icons-material';
import { Grid } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { getSharedBusinessPlacements } from 'lib/api/multiverse/fund';
import TableCard from 'lib/designSystem/Card/TableCard';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS, CHART_COLORS } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

const ARREAR_COLORS = [
  CHART_COLORS.green,
  CHART_COLORS.yellow,
  CHART_COLORS.orange,
  CHART_COLORS.amber,
  CHART_COLORS.red,
];

const Placements = () => {
  const dispatch = useDispatch();
  const { companyId, businessIdentifier } = useParams();

  if (!companyId || !businessIdentifier) return null;

  const defaultEndDate = dayjs();
  const defaultStartDate = dayjs().subtract(6, 'days');
  const [startDate, setStartDate] = useState<Dayjs>(defaultStartDate);
  const [endDate, setEndDate] = useState<Dayjs>(defaultEndDate);
  const [businessSegmentType, setBusinessSegmentType] = useState<
    'economic' | 'sales'
  >('economic');
  const [stakeholderSegmentType, setStakeholderSegmentType] = useState<
    'economic' | 'sales'
  >('economic');

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (startDate) {
      params.append('startDate', startDate.format('YYYY-MM-DD'));
    }
    if (endDate) {
      params.append('endDate', endDate.format('YYYY-MM-DD'));
    }
    return params.toString();
  }, [startDate, endDate]);

  const { data, isPending } = getSharedBusinessPlacements(
    dispatch,
    businessIdentifier,
    companyId,
    query,
  );

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <Grid container spacing={2} my={1}>
      <Grid container item xs={12} spacing={2}>
        <Grid container item xs={9} spacing={2}>
          <Grid item xs={3.5}>
            <StatCard
              label="Total colocaciones"
              value={formatMoney(data?.statistics.totalActivePlacement || 0)}
              valueLabel="CLP"
              color={BASE_COLORS.blue}
              icon={<AttachMoney sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={2.5}>
            <StatCard
              label="Documentos activos"
              value={(
                data?.statistics.activeDocumentsCount || 0
              ).toLocaleString('es-CL')}
              valueLabel="docs"
              color={BASE_COLORS.green}
              icon={<DescriptionOutlined sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={2.5}>
            <StatCard
              label="Financiamiento"
              value={data?.statistics.averageHoursUntilActivation ?? 0}
              valueLabel="hora(s)"
              color={BASE_COLORS.purple}
              icon={<AccessTime sx={{ fontSize: 24 }} />}
            />
          </Grid>
          <Grid item xs={3.5}>
            <StatCard
              label="Promedio documento"
              value={formatMoney(
                data?.statistics.averageAmountPerDocument || 0,
              )}
              valueLabel="CLP"
              color={BASE_COLORS.purple}
              icon={<Calculate sx={{ fontSize: 24 }} />}
            />
          </Grid>

          <Grid item xs={12}>
            <TableCard>
              <SubtitleWithTooltip
                title="Colocación en el tiempo"
                tooltipContent="Estos son los montos de colocación por estado de mora y fuente de financiamiento a lo largo del tiempo."
                tooltipPlacement="right"
                variant="compact"
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha inicio"
                    value={startDate}
                    onChange={(newValue) => {
                      if (newValue) setStartDate(newValue);
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Fecha término"
                    value={endDate}
                    onChange={(newValue) => {
                      if (newValue) setEndDate(newValue);
                    }}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                      },
                    }}
                  />
                </LocalizationProvider>
              </SubtitleWithTooltip>
              <LineChart
                key="placements"
                data={data?.lineChartData || []}
                showLegend={true}
                showArea={false}
                height={450}
              />
            </TableCard>
          </Grid>
        </Grid>

        <Grid item xs={3}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TableCard>
                <SubtitleWithTooltip
                  title="Estado de mora"
                  tooltipContent="Estos son los montos de morosidad por estado de mora"
                  tooltipPlacement="right"
                  variant="compact"
                />
                <CentralPieChart
                  data={data?.arrearPieChartData || []}
                  height={200}
                  colors={ARREAR_COLORS}
                />
              </TableCard>
            </Grid>
            <Grid item xs={12}>
              <TableCard>
                <SubtitleWithTooltip
                  title="Fuente financiamiento"
                  tooltipContent="Estos son los montos de colocación por fuente de financiamiento"
                  tooltipPlacement="right"
                  variant="compact"
                />
                <CentralPieChart
                  data={data?.placementPieChartData || []}
                  height={200}
                />
              </TableCard>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container item xs={12} spacing={2}>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Clientes"
              tooltipContent="Desglose de colocación por segmento económico o de venta del cliente"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={businessSegmentType}
                onChange={(value) =>
                  setBusinessSegmentType(value as 'economic' | 'sales')
                }
                options={[
                  {
                    value: 'economic',
                    label: 'Económico',
                    ariaLabel: 'economic',
                  },
                  { value: 'sales', label: 'Venta', ariaLabel: 'sales' },
                ]}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                businessSegmentType === 'economic'
                  ? data?.businessEconomicSegmentPieChartData || []
                  : data?.businessSalesSegmentPieChartData || []
              }
              height={240}
            />
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Deudores"
              tooltipContent="Desglose de colocación por segmento económico o de venta del deudor"
              tooltipPlacement="right"
              variant="compact"
            >
              <CustomToggleButtonGroup
                value={stakeholderSegmentType}
                onChange={(value) =>
                  setStakeholderSegmentType(value as 'economic' | 'sales')
                }
                options={[
                  {
                    value: 'economic',
                    label: 'Económico',
                    ariaLabel: 'economic',
                  },
                  { value: 'sales', label: 'Venta', ariaLabel: 'sales' },
                ]}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                stakeholderSegmentType === 'economic'
                  ? data?.stakeholderEconomicSegmentPieChartData || []
                  : data?.stakeholderSalesSegmentPieChartData || []
              }
              height={240}
            />
          </TableCard>
        </Grid>
        <Grid item xs={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Colocación por tipo"
              tooltipContent="Estos son los montos de colocación por tipo de operación"
              tooltipPlacement="right"
              variant="compact"
            />
            <CentralPieChart
              data={data?.statistics.placementByType || []}
              height={240}
            />
          </TableCard>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Placements;
